import type { BreadcrumbItem } from '@common/Breadcrumb/Breadcrumb';
import { JsonLd } from '@common/JsonLd/JsonLd';
import { UrgentMessage } from '@common/UrgentMessage/UrgentMessage';
import type { AttributeDefinitionFragment, DeliveryTimeFragment, Teasers } from '@generated/graphql';
import { useProductPageStore } from '@hooks/states/useProductPageStore';
import type { VariantAxis } from '@hooks/states/variantSelectionSlice';
import { useHasCurrentLocale } from '@hooks/useHasCurrentLocale';
import { usePriceOverwrites } from '@hooks/usePriceOverwrites';
import { useRestoreFromShoppingCartEdit } from '@hooks/useRestoreFromShoppingCartEdit';
import { ToastType, useToasts } from '@hooks/useToasts';
import {
  DEFAULT_CURRENCY,
  getLocale,
  PRIMARY_CATEGORY,
  WIDEN_DEFAULT_OPENGRAPH_IMAGE_HEIGHT,
  WIDEN_DEFAULT_OPENGRAPH_IMAGE_WIDTH,
} from '@libs/constants';
import { BaseLayout } from '@layout/BaseLayout/BaseLayout';
import { AssociationType } from '@libs/associations';
import type { LabeledAttribute, ProductAttribute } from '@libs/attribute.helper';
import { getAttributeValue, getAttributeValueAsString } from '@libs/attribute.helper';
import { getCFCasePrice } from '@libs/cfCase.helper';
import { sortByMediaElementTypeSortOrder } from '@libs/comparer.helper';
import { type ModelById } from '@libs/graphql.helper.server';
import { getCanonical } from '@libs/i18n.helper';
import type { VideoObjectJsonLd } from '@libs/json-ld';
import {
  getPriceDisplayTypeForOrderType,
  overwritePricesForAlternativeAndSuitableProps,
} from '@libs/product-price-utils';
import {
  getAssociatedModelIdsFromProduct,
  getAssociationProductReferences,
  getProductIdForLocale,
  getProductMetaDescription,
  getProductPriceType,
  getProductTitle,
  isEchainAccessory,
} from '@libs/product.helper';
import { getWebtrendsParameters } from '@libs/seo.helper';
import { findNewVariantAxesByCategory, generateAxisItems, getSkuOfMostSimilarProduct } from '@libs/variant-axes.helper';
import type { WidenMediaElement } from '@libs/widen-utils';
import { WidenUtil } from '@libs/widen-utils';
import type { NodeContact } from '@models/api/node-contact.types';
import type { Footer } from '@models/api/page-content.types';
import type { Category, LanguageAlternate } from '@models/data';
import type {
  PriceFragmentEnhanced,
  ProductFragmentEnhanced,
  ProductPrice,
  MinimalProductWithVariantAxesAttributes,
} from '@models/enhanced-fragments';
import type { CertificateIcon, CopperPrice, GraphQlPriceType } from '@models/graphql';
import { ProductHelper } from '@models/graphql';
import { WidenImageConfig } from '@models/widen-image-config';
import type { AlternativeProductProps } from '@product/AlternativeProduct/AlternativeProduct';
import { getSuitableProducts } from '@product/ProductPage/PageLayouts/getSuitableProducts';
import { useUniqueAssociatedProductsQuery } from '@product/ProductPage/PageLayouts/hooks/useUniqueAssociatedProductsQuery';
import type { SuitableProductProps } from '@product/SuitableProduct/SuitableProduct';
import * as Sentry from '@sentry/nextjs';
import { useTranslation } from 'next-i18next';
import { NextSeo } from 'next-seo';
import { useRouter } from 'next/router';
import React, { useCallback, useEffect, useMemo } from 'react';
import type { DehydratedState } from 'react-query';
import type { Product, WithContext } from 'schema-dts';
import { DefaultProductPageLayout } from './PageLayouts/DefaultProductPageLayout';
import { EChainProductPageLayout } from './PageLayouts/EChainProductPageLayout';
import { extractCFCaseProps } from './PageLayouts/extractCFCaseProps';
import { IGUS_REWRITE } from '@libs/redirect.helper';
import type { UnitSystem } from '@icommerce/units';
import { useProductPageUrl } from './hooks/useProductPageUrl';
import type { HeaderData } from '@layout/Header/types';
import { getDeliveryTimeLocale } from '@libs/utils';
import { getPriceCountry } from '@libs/get-price-country.helper';
import { getCurrencyForPriceCountry } from '@libs/currencies';
import { useProductData } from './hooks/useProductData';
import { getProductImages } from '@page-helpers/getProductImages';
import { getFeatureFlagValue } from '@libs/abby/abby';
import { Logger } from '@icommerce/logger';
import { useHasArtnrParam } from './hooks/useHasArtnrParam';
import type { MetaTag } from 'next-seo/lib/types';
import { useAppDataStore } from '@hooks/states/useAppDataStore';

export interface ProductPageProps {
  productJsonLd: WithContext<Product>;
  videoJsonLd: VideoObjectJsonLd[];
  // eslint-disable-next-line react/no-unused-prop-types
  currentProduct: ProductFragmentEnhanced;
  selectableVariantAxesAttributesDefinitions: AttributeDefinitionFragment[];
  dailyCopperPriceDefinition: CopperPrice[];
  marketingTeasers: Teasers[];
  languageAlternates: LanguageAlternate[];
  // eslint-disable-next-line react/no-unused-prop-types
  unitSystem: UnitSystem;
  alternativeModelProductsByModelId: Record<string, ModelById>;
  // eslint-disable-next-line react/no-unused-prop-types
  availableVariantAxes?: VariantAxis[];
  selectedVariant?: string;
  // eslint-disable-next-line react/no-unused-prop-types
  selectedVariantAxesValues?: Map<string, string>;
  header: HeaderData;
  footer: Footer | null;
  // eslint-disable-next-line react/no-unused-prop-types
  locale?: string;
  categoryTree: Category;
}

// ExtendedProductPageProps contains all props that are passed to the ProductPage component (ProductPageProps base interface), as well as all properties
// that will be returned from getStaticProps, but are not passed to component and are placed in the zustand store inbetween.
export interface ExtendedProductPageProps extends ProductPageProps {
  priceType: GraphQlPriceType;
  allProducts: MinimalProductWithVariantAxesAttributes[];
  dehydratedState: DehydratedState;
  doApplicationExamplesExist: boolean;
  modelKey: string;
  breadcrumbItems: BreadcrumbItem[];
  certificateIcons: CertificateIcon[];
  contact: NodeContact | null;
  currentProduct: ProductFragmentEnhanced;
  primaryCategoryCode: string;
  echainAttributeDefinitions: AttributeDefinitionFragment[];
  onlineToolsDefinition: Teasers[];
}

// This interface should contain all properties that are shared between all different page layouts.
// Properties specific to a single layout will be defined in the respective layout component.
export interface SharedProductPageProps {
  prices: SharedProductPagePropsPrices;
  dailyCopperPriceDefinition: CopperPrice[];
  carouselImages: WidenMediaElement[];
}

export interface SharedProductPagePropsPrices {
  fetchingDiscountPricesStatus: 'idle' | 'error' | 'loading' | 'success';
  unitBasePrice: number;
  unitLengthPrice: number | undefined;
  discountQuantityPrices: PriceFragmentEnhanced[];
  associatedProductPrices: AssociatedProductPrice[];
  currencyCode: string;
}

export interface AssociatedProductPrice {
  id: string;
  baseUnitCentPrice: number | null;
  lengthUnitCentPrice: number | null;
  quantityDiscounts: Array<PriceFragmentEnhanced>;
}

export const ProductPage: React.FC<ProductPageProps> = ({
  languageAlternates,
  productJsonLd,
  videoJsonLd,
  selectableVariantAxesAttributesDefinitions,
  dailyCopperPriceDefinition,
  marketingTeasers,
  alternativeModelProductsByModelId,
  selectedVariant,
  header,
  footer,
  categoryTree,
}) => {
  // **************************************
  // Initialize store states and setters
  // **************************************
  const allProducts = useProductPageStore(state => state.allProducts);
  const availableVariantAxes = useProductPageStore(state => state.availableVariantAxes);
  const breadcrumbItems = useProductPageStore(state => state.breadcrumbItems);
  const contact = useProductPageStore(state => state.contact);
  const currentProduct = useProductPageStore(state => state.currentProduct);
  const lastModifiedVariantAxisValue = useProductPageStore(state => state.lastModifiedVariantAxisValue);
  const primaryCategoryCode = useProductPageStore(state => state.primaryCategoryCode);
  const productImages = useProductPageStore(state => state.productImages);
  const selectedVariantSku = useProductPageStore(state => state.selectedVariantSku);
  useProductPageUrl();

  const setAvailableVariantAxes = useProductPageStore(state => state.setAvailableVariantAxes);
  const setCurrentProduct = useProductPageStore(state => state.setCurrentProduct);
  const setPriceData = useProductPageStore(state => state.setPriceData);
  const setSelectedVariantSku = useProductPageStore(state => state.setSelectedVariantSku);
  const setProductImages = useProductPageStore(state => state.setProductImages);
  const setBuyBoxReady = useProductPageStore(state => state.setBuyBoxReady);

  // **************************************
  // External hooks
  // **************************************
  const { addToast } = useToasts();
  const router = useRouter();
  const locale = getLocale(router);
  const deliveryTimeLocale = getDeliveryTimeLocale(locale);
  const priceCountry = getPriceCountry(locale);
  const currency = getCurrencyForPriceCountry(priceCountry) || DEFAULT_CURRENCY;
  const { t } = useTranslation(['product/productDetails', 'toast/toast', 'layout/general']);

  const { setCurrentCategoryTree } = useAppDataStore.getState();
  setCurrentCategoryTree(categoryTree);

  // **************************************
  // Initialize product page data
  // **************************************
  const isEchainPage = primaryCategoryCode === PRIMARY_CATEGORY.ECHAIN;

  // Fetch uniqueAssociatedProducts
  const { data: uniqueAssociatedProducts, isLoading: isLoadingUniqueAssociatedProducts } =
    useUniqueAssociatedProductsQuery();

  useRestoreFromShoppingCartEdit();

  const productReleases = useMemo(
    () =>
      currentProduct?.product_releases?.reduce<Record<string, boolean>>(
        (aggregate: Record<string, boolean>, release) => ({
          ...aggregate,
          [release.locale_code]: !!release.released,
        }),
        {}
      ) ?? {},
    [currentProduct]
  );

  // check if the product is released in the locale (or has a version in old page)
  const runtimelanguageAlternates: LanguageAlternate[] = useMemo(
    () =>
      languageAlternates.filter(
        alternate =>
          productReleases[alternate.hrefLang] ||
          currentProduct.attributes?.product_ID_CMS ||
          alternate.hrefLang === 'x-default'
      ),
    [languageAlternates, productReleases, currentProduct.attributes]
  );

  const alternatesForLanguageSwitch = useMemo(
    () =>
      runtimelanguageAlternates.filter(
        alternate => productReleases[alternate.hrefLang] || currentProduct.attributes?.product_ID_CMS
      ),
    [runtimelanguageAlternates, productReleases, currentProduct.attributes]
  );

  const productIdsForPriceOverwrites: string[] = [];

  const associatedProducts = useMemo(
    () =>
      !isLoadingUniqueAssociatedProducts && uniqueAssociatedProducts
        ? getAssociationProductReferences(currentProduct, [AssociationType.ALTERNATIVE_PRODUCT])
            ?.map(associationReference => uniqueAssociatedProducts[associationReference.id])
            .filter(product => product) ?? []
        : [],
    [isLoadingUniqueAssociatedProducts, uniqueAssociatedProducts, currentProduct]
  );

  let alternativeProducts: AlternativeProductProps[] =
    associatedProducts?.map(alternative_product => {
      const productIdDisplayName = getProductIdForLocale(alternative_product, locale);
      productIdsForPriceOverwrites.push(productIdDisplayName);
      return {
        title: getAttributeValueAsString(
          alternative_product.attributes?.series_description as ProductAttribute<string>
        ),
        slug: alternative_product.model_id,
        singleUsp: getAttributeValueAsString(
          alternative_product.attributes?.attr_single_usp as ProductAttribute<string>
        ),
        new: Boolean(
          getAttributeValue<boolean>(
            alternative_product.attributes?.attr_status_new_product_boolean as ProductAttribute<boolean>
          )
        ),
        primaryCategoryCode: getAttributeValue<string>(
          alternative_product.attributes.main_category_key as ProductAttribute<string>
        ),
        image: ProductHelper.getTeaserImage(alternative_product) || null,
        priceType: getPriceDisplayTypeForOrderType(
          getAttributeValue<string>(alternative_product.attributes?.order_type as ProductAttribute<string>)
        ),
        prices: alternative_product.aggregated_product_prices?.[0]?.prices || null,
        deliveryTimes: alternative_product.delivery_times,
        productId: alternative_product.id,
        productIdDisplayName,
      };
    }) ?? [];

  const alternativeModelIds =
    getAssociatedModelIdsFromProduct(currentProduct, AssociationType.ALTERNATIVE_PRODUCT) ?? [];

  alternativeModelIds.forEach(associatedModelId => {
    if (alternativeModelProductsByModelId[associatedModelId]) {
      const modelById = alternativeModelProductsByModelId[associatedModelId];
      productIdsForPriceOverwrites.push(getProductIdForLocale(modelById.selected_product, locale)); // required for overwrite of prices

      const deliveryTimes: DeliveryTimeFragment[] = [];
      if (modelById.best_product_delivery_time) {
        deliveryTimes.push({
          code: (modelById.best_product_delivery_time as { code: DeliveryTimeFragment['code'] })?.code,
          label: (modelById.best_product_delivery_time as { text: DeliveryTimeFragment['label'] })?.text,
        } as DeliveryTimeFragment);
      }

      alternativeProducts?.push({
        title: getAttributeValueAsString(modelById.selected_product.description),
        slug: modelById.model_id,
        singleUsp: getAttributeValueAsString(modelById.selected_product.single_usp),
        new: Boolean(modelById.is_new),
        image:
          ProductHelper.getTeaserImageFromRawData(
            modelById.selected_product.description,
            modelById.selected_product.assets
          ) || null,
        priceType: getPriceDisplayTypeForOrderType(getAttributeValue<string>(modelById.selected_product.order_type)),
        prices: modelById.best_price_by_regular_price as ProductPrice,
        deliveryTimes,
        productId: modelById.selected_product.id,
        productIdDisplayName: getProductIdForLocale(modelById.selected_product, locale),
      } as AlternativeProductProps);
    }
  });

  const cfCasesOfCurrentProduct = useMemo(() => {
    if (isLoadingUniqueAssociatedProducts || !uniqueAssociatedProducts) {
      return [];
    }
    const { cfCases: cfs } = extractCFCaseProps(currentProduct, uniqueAssociatedProducts);
    return cfs;
  }, [currentProduct, isLoadingUniqueAssociatedProducts, uniqueAssociatedProducts]);

  // add CFCase to productIdsForPriceOverwrites
  if (cfCasesOfCurrentProduct) {
    for (const cfCase of cfCasesOfCurrentProduct) {
      const cfCaseProduct = uniqueAssociatedProducts?.[cfCase.sku];
      productIdsForPriceOverwrites.push(getProductIdForLocale(cfCaseProduct, locale));
    }
  }

  // Add suitable products to productIdsForPriceOverwrites in order to fetch prices dynamically
  let suitableProducts: SuitableProductProps[] = useMemo(() => {
    if (isLoadingUniqueAssociatedProducts || !uniqueAssociatedProducts) {
      return [];
    }

    return getSuitableProducts(currentProduct, uniqueAssociatedProducts, locale);
  }, [isLoadingUniqueAssociatedProducts, uniqueAssociatedProducts, currentProduct, locale]);

  suitableProducts.forEach(suitableProduct => {
    if (!suitableProduct.prices) {
      Sentry.captureException(new Error(`Missing suitable-product prices for "${suitableProduct.title}"`));
    }
  });

  suitableProducts?.forEach(suitableProduct => {
    if (suitableProduct.productId) {
      productIdsForPriceOverwrites.push(suitableProduct.productIdDisplayName || suitableProduct.productId);
    }
  });

  // retrieve prices for associated products dynamically
  const prices = usePriceOverwrites(productIdsForPriceOverwrites, locale, () =>
    addToast({ content: t('TOAST.ERROR_OCCURRED', { ns: 'toast/toast' }), type: ToastType.ERROR })
  );

  useEffect(() => {
    setPriceData({
      basePricePerUnit: prices.unitBasePrice,
      additionalLengthPrice: prices.unitLengthPrice || null,
      discountQuantity: prices.discountQuantityPrices,
      currencyCode: prices.currencyCode,
      fetchingDiscountPricesStatus: prices.fetchingDiscountPricesStatus,
      priceType: getProductPriceType(currentProduct),
    });
  }, [
    currentProduct,
    prices.currencyCode,
    prices.discountQuantityPrices,
    prices.fetchingDiscountPricesStatus,
    prices.unitBasePrice,
    prices.unitLengthPrice,
    setPriceData,
  ]);

  // overwrite CFCase prices
  if (cfCasesOfCurrentProduct) {
    for (const cfCase of cfCasesOfCurrentProduct) {
      const cfCasePrices = prices.associatedProductPrices?.find(p => p.id === cfCase.sku);
      if (cfCasePrices && cfCasePrices.quantityDiscounts && cfCasePrices.quantityDiscounts[0]) {
        const props = cfCasesOfCurrentProduct.find(value => value.sku === cfCase.sku);
        if (props) {
          props.pricePerUnit = getCFCasePrice(cfCasePrices.quantityDiscounts[0]).pricePerUnit;
          props.currencyCode = getCFCasePrice(cfCasePrices.quantityDiscounts[0]).currencyCode;
        }
      }
    }
  }

  // overwrite alternativeProducts product prices
  if (alternativeProducts) {
    alternativeProducts = overwritePricesForAlternativeAndSuitableProps(
      alternativeProducts,
      prices
    ) as AlternativeProductProps[];
  }

  // overwrite suitable product prices
  if (suitableProducts) {
    suitableProducts = overwritePricesForAlternativeAndSuitableProps(
      suitableProducts,
      prices
    ) as SuitableProductProps[];
  }

  // Webtrends parameters
  const webtrendsTags = getWebtrendsParameters(
    getAttributeValue<string>(currentProduct.attributes?.WT_cg_n as ProductAttribute<string>),
    getAttributeValue<string>(currentProduct.attributes?.WT_cg_s as ProductAttribute<string>)
  );

  // Marketing Cloud parameters
  const marketingCloudTags: MetaTag[] = [
    {
      name: 'sis-tag_page-type',
      content: 'Shop',
    },
    {
      name: 'sis-tag_page-subtype',
      content: 'Product Detail Page',
    },
    {
      name: 'sis-tag_article-number',
      content: currentProduct.id,
    },
    {
      name: 'sis-tag_availability-code',
      content: currentProduct.delivery_times?.[0]?.code?.toString(),
    },
    {
      name: 'sis-tag_availability-text',
      content: currentProduct.delivery_times?.[0]?.label,
    },
    currentProduct.attributes.product_group1_AX && {
      name: 'tag_product_axis_ID_prod_cat_1',
      content: (currentProduct.attributes.product_group1_AX as LabeledAttribute<string> | undefined).value_key,
    },
    currentProduct.attributes.product_group2_AX && {
      name: 'tag_product_axis_ID_prod_cat_2',
      content: (currentProduct.attributes.product_group2_AX as LabeledAttribute<string> | undefined).value_key,
    },
    currentProduct.attributes.product_group3_AX && {
      name: 'tag_product_axis_ID_prod_cat_3',
      content: (currentProduct.attributes.product_group3_AX as LabeledAttribute<string> | undefined).value_key,
    },
  ].filter(Boolean);

  const carouselImages: WidenMediaElement[] = productImages[currentProduct.id];
  // First element of the carousel is the Largest Contentful Paint (LCP) and should be preloaded
  // See https://nextjs.org/docs/api-reference/next/image#priority
  carouselImages && carouselImages.length > 0 && (carouselImages[0].priority = true);

  // sort carousel images by given sort order in mediaElementTypeSortOrder
  if (carouselImages?.length) {
    carouselImages.sort((media1, media2) => sortByMediaElementTypeSortOrder(media1, media2));
  }

  // generate opengraph data
  const openGraphData = useMemo(() => {
    const productIdDisplayName = getProductIdForLocale(currentProduct, locale);
    let graphData = {
      type: 'website',
      url: `${IGUS_REWRITE(getCanonical(router.asPath, locale), locale)}?artnr=${productIdDisplayName || ''}`,
    };

    if (Object.keys(productImages).length > 0 && selectedVariant) {
      const openGraphImageProps = productImages[String(selectedVariant)]?.[0];

      if (!openGraphImageProps) {
        return graphData;
      }

      const imageSrc = WidenUtil.generateWidenImageUrl(
        new WidenImageConfig(
          openGraphImageProps.shortCode ?? '',
          openGraphImageProps.filename ?? '',
          WIDEN_DEFAULT_OPENGRAPH_IMAGE_WIDTH,
          WIDEN_DEFAULT_OPENGRAPH_IMAGE_HEIGHT
        )
      );

      graphData = Object.assign(graphData, {
        images: [
          {
            url: imageSrc,
            alt: openGraphImageProps.title ?? '',
          },
        ],
      });
    }
    return graphData;
  }, [router.asPath, locale, productImages, selectedVariant, currentProduct]);

  // **************************************
  // Effect Hooks
  // **************************************
  // Set selectedVariantSku (currentProduct) based off of the URL query param
  useHasArtnrParam();

  const updateVariantAxes = useCallback(
    (availableVariantAxesUpdate: VariantAxis[] | undefined) => {
      // update items of variant axes
      const newAvailableVariantAxes = availableVariantAxesUpdate?.map((axis, index): VariantAxis => {
        const variantAxisDefinition = selectableVariantAxesAttributesDefinitions.find(
          definition => definition.attribute_id === axis.attribute
        );

        return {
          ...axis,
          ...(!axis.readonly &&
            variantAxisDefinition && {
              items: generateAxisItems(
                index,
                allProducts,
                selectableVariantAxesAttributesDefinitions,
                variantAxisDefinition,
                currentProduct
              ),
            }),
        };
      });

      if (newAvailableVariantAxes) {
        setAvailableVariantAxes(
          findNewVariantAxesByCategory(
            primaryCategoryCode,
            newAvailableVariantAxes,
            allProducts,
            selectableVariantAxesAttributesDefinitions,
            currentProduct
          )
        );
      }
    },
    [currentProduct, selectableVariantAxesAttributesDefinitions, setAvailableVariantAxes, primaryCategoryCode]
  );

  // replace the internal next.js routing on history-back to avoid shallow
  // routing and a broken state.
  useEffect(() => {
    router.beforePopState(args => {
      if (typeof window !== 'undefined') {
        window.location.href = args.as;
      }
      return false;
    });
  }, [router]);

  const { data } = useProductData(selectedVariantSku, priceCountry, currency, deliveryTimeLocale, locale);
  useEffect(() => {
    if (data) {
      setCurrentProduct(data);
      setProductImages(getProductImages([data], locale));
      setBuyBoxReady(true);
    }
  }, [data, selectedVariantSku]);

  // when the currently selected variant changes, set the new availableVariantAxes accordingly
  // do not include availableVariantAxes in effect dependencies because this might otherwise result in a rerender loop
  // if the currentProduct does not match the new selectedVariantSku, update it as well
  useEffect(() => {
    updateVariantAxes(availableVariantAxes);
    /**
     * Evergage is loaded into our pages dynamically through Piwik. We do not know the full object and are 'mocking' its type in evergage.d.ts.
     * The reinit() function has to be used here to update the Evergage instance once the product variant has changed.
     */
    try {
      if (getFeatureFlagValue('useEvergage') && typeof window !== 'undefined') {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-explicit-any
        const evergage = (window as any).Evergage;
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        if (evergage && typeof evergage.reinit === 'function') {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
          evergage.reinit();
        }
      }
    } catch (e) {
      const error = e as Error;
      Logger.error({ msg: error.message });
    }
  }, [selectedVariantSku, updateVariantAxes]);

  // when the user changes a value in any of the variant axes (which we know is the case when lastModifiedVariantAxisValue changes)
  // then get the product that matches the selected dropdowns the best, and update the selected variant to be this product
  // do not update the url directly here, because there is another hook taking care of that
  useEffect(() => {
    if (!lastModifiedVariantAxisValue) {
      return;
    }

    const sku = getSkuOfMostSimilarProduct(
      currentProduct,
      allProducts,
      selectableVariantAxesAttributesDefinitions,
      lastModifiedVariantAxisValue.value,
      lastModifiedVariantAxisValue.key
    );
    if (sku) {
      setSelectedVariantSku(sku);
    }
  }, [lastModifiedVariantAxisValue]);

  const renderPageContent = () =>
    isEchainPage ? (
      <EChainProductPageLayout
        prices={prices}
        dailyCopperPriceDefinition={dailyCopperPriceDefinition}
        carouselImages={carouselImages}
      />
    ) : (
      <DefaultProductPageLayout
        prices={prices}
        dailyCopperPriceDefinition={dailyCopperPriceDefinition}
        suitableProducts={suitableProducts}
        marketingTeasers={marketingTeasers}
        carouselImages={carouselImages}
        alternativeProducts={alternativeProducts}
      />
    );

  // enable the UrgentMessage on these domains
  // to be able to enable/ disable specific domains fast
  const isEnabledDomain = useHasCurrentLocale([]);

  const metaTitleAkeneo = getAttributeValue<string | undefined>(
    currentProduct?.attributes?.attr_meta_title as ProductAttribute<string>
  );

  return (
    <BaseLayout
      breadcrumbItems={breadcrumbItems}
      header={{ ...header, currentProduct, languageAlternates: alternatesForLanguageSwitch }}
      contact={contact}
      footer={footer}
    >
      {videoJsonLd.map(vjld => (
        <JsonLd key={vjld.name} id={`${vjld.name}-video-json-ld`} data={vjld} />
      ))}
      <JsonLd id={`${selectedVariant || ''}-product-json-ld`} data={productJsonLd} />
      <NextSeo
        noindex={isEchainAccessory(breadcrumbItems)}
        openGraph={openGraphData}
        title={metaTitleAkeneo || getProductTitle(currentProduct)}
        description={
          getAttributeValue<string | undefined>(
            currentProduct.attributes?.attr_meta_description as ProductAttribute<string>
          ) || getProductMetaDescription(currentProduct)
        }
        canonical={IGUS_REWRITE(getCanonical(router.asPath, locale), locale)}
        languageAlternates={runtimelanguageAlternates}
        additionalMetaTags={[...webtrendsTags, ...marketingCloudTags]}
      />
      {isEnabledDomain && <UrgentMessage />}
      {renderPageContent()}
    </BaseLayout>
  );
};
